import type { JWTPayload as BasicJWTPayload } from "jose";
import { flare } from "@flareapp/js";
import { useLocalStorage } from "@vueuse/core";
import axios from "axios";
import { decodeJwt } from "jose";
import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import { login as loginCall, refresh as refreshCall } from "~/lib/api/auth";
import { md5 } from "~/lib/hashing";

export type JWTPayload = BasicJWTPayload & {
    app_id: number;
    features: Record<string, boolean>;
    is_admin: boolean;
    is_owner: boolean;
    login_url: string;
    permissions: string[];
    prv: string;
    user_id: number;
};

axios.defaults.headers.common = {
    Accept: "application/json",
    "Access-Control-Allow-Origin": "*",
    "Content-Type": "application/json",
};
const oneMinute = 60 * 1000;

export const useTokenStore = defineStore("tokenStore", () => {
    const timer = ref<ReturnType<typeof setTimeout>>();
    const token = useLocalStorage<string | undefined>(
        "master-administration-tokenStore-token",
        undefined,
    );

    const refreshToken = useLocalStorage<string | undefined>(
        "master-administration-tokenStore-refreshToken",
        undefined,
    );

    const refreshTokenTtl = useLocalStorage<string | undefined>(
        "master-administration-tokenStore-refreshTokenTtl",
        undefined,
    );

    const tokenLogoutHash = useLocalStorage<string | undefined>(
        "master-administration-tokenStore-tokenLogoutHash",
        undefined,
    );

    const now = useNow({ interval: oneMinute });

    const isRefreshTokenExpired = computed<boolean>(() => {
        if (refreshTokenTtl.value === undefined) return true;

        const refreshTokenExpiryDate = new Date(refreshTokenTtl.value);

        if (isNaN(refreshTokenExpiryDate.getTime())) return true;

        return refreshTokenExpiryDate.getTime() - now.value.getTime() < 0;
    });

    const isTokenExpired = computed<boolean>(() => {
        if (payload.value === undefined || payload.value.exp === undefined) {
            return true;
        }

        return payload.value.exp * 1000 - now.value.getTime() - oneMinute < 0;
    });

    const isLoggedIn = computed<boolean>(
        () =>
            token.value !== undefined &&
            (!isTokenExpired.value || !isRefreshTokenExpired.value),
    );

    function isRedirectParameterValid(redirectPath: string) {
        if (!redirectPath.startsWith("/") || redirectPath.startsWith("//")) {
            return false;
        }

        try {
            const redirectUrl = new URL(redirectPath, "https://smake.local");

            return !["/entrance", "/login"].includes(
                redirectUrl.pathname.toLowerCase(),
            );
        } catch {
            return false;
        }
    }

    const { locale } = storeToRefs(useLocalSettingsStore());

    const config = useRuntimeConfig();
    const baseUrl = computed(() => `${config.public.masterApiBaseUrl}`);

    watchEffect(() => {
        axios.defaults.baseURL = baseUrl.value;
    });

    const payload = computed(() => {
        if (!token.value) return undefined;

        try {
            return decodeJwt<JWTPayload>(token.value);
        } catch {
            return;
        }
    });

    const queryClient = useQueryClient();
    let previousUserId = payload.value?.user_id;
    watch(
        () => payload.value?.user_id,
        (newUserId) => {
            if (newUserId === previousUserId) return;
            previousUserId = newUserId;
            void queryClient.resetQueries();
        },
    );

    watch([token, refreshToken, refreshTokenTtl], ([newToken]) => {
        const route = useRoute();
        if (!newToken && !isGuestPage(route)) {
            void navigateToLogin();
        }
    });

    watchEffect(() => {
        if (isTokenExpired.value && !isRefreshTokenExpired.value) {
            if (
                tokenLogoutHash.value &&
                md5(token.value ?? "") === tokenLogoutHash.value
            ) {
                void logout();
            } else {
                void refresh();
            }
        }
    });

    async function navigateToLogin() {
        const fullPath = useRoute().fullPath;

        return await navigateTo({
            name: "login",
            query: { redirect: fullPath },
        });
    }

    async function logout() {
        if (
            token.value === undefined &&
            refreshToken.value === undefined &&
            refreshTokenTtl.value === undefined
        ) {
            await navigateToLogin();

            return;
        }

        if (token.value) {
            tokenLogoutHash.value = md5(token.value);
        }

        setToken();

        await navigateToLogin();
    }

    function startRefreshTokenTimer() {
        if (timer.value) clearTimeout(timer.value);

        if (!payload.value?.exp) {
            throw new Error('"exp" not set in JWT!');
        }

        const timeout =
            payload.value?.exp * 1000 - now.value.getTime() - oneMinute;
        timer.value = setTimeout(() => {
            refresh().catch((error) => {
                console.log(error);
            });
        }, timeout);
    }

    function setToken(tokenData?: {
        locale?: string;
        refreshToken?: string;
        refreshTokenTtl?: string;
        token?: string;
    }) {
        refreshToken.value = tokenData?.refreshToken;
        refreshTokenTtl.value = tokenData?.refreshTokenTtl;
        token.value = tokenData?.token;

        axios.defaults.headers.common.Authorization = `Bearer ${token.value}`;

        if (tokenData?.locale) {
            locale.value = tokenData.locale;
            axios.defaults.headers.common["Accept-Language"] = tokenData.locale;
        }

        if (token.value && payload.value?.exp) startRefreshTokenTimer();
        else if (timer.value) clearTimeout(timer.value);
    }

    function refreshIfNecessary() {
        if (!isTokenExpired.value) {
            return;
        }

        return refresh();
    }

    async function refresh() {
        if (!isRefreshTokenExpired.value) {
            if (
                tokenLogoutHash.value &&
                md5(token.value ?? "") === tokenLogoutHash.value
            ) {
                await navigateToLogin();

                return;
            }

            try {
                const refreshResponse = await refreshCall(refreshToken.value);
                setToken({
                    refreshToken: refreshResponse.refresh_token,
                    refreshTokenTtl: refreshResponse.refresh_token_ttl,
                    token: refreshResponse.access_token,
                });

                return;
            } catch {
                await navigateToLogin();
            }
        }

        await navigateToLogin();
    }

    async function login(
        email: string,
        password: string,
        intendedRoute?: string,
    ) {
        const loginData = await loginCall({ email, password });

        if (
            loginData.access_token &&
            loginData.refresh_token &&
            loginData.refresh_token_ttl
        ) {
            setToken({
                refreshToken: loginData.refresh_token,
                refreshTokenTtl: loginData.refresh_token_ttl,
                token: loginData.access_token,
            });
            tokenLogoutHash.value = undefined;

            await attemptNavigateToRedirect(intendedRoute);
        } else {
            await flare.report(
                new Error("Login was correct but no Token was received"),
            );
        }
    }

    async function attemptNavigateToRedirect(intendedRoute?: string) {
        const route = useRoute();

        const decodedRedirect = decodeURIComponent(
            intendedRoute ?? (route.query.redirect as string) ?? "",
        );

        if (decodedRedirect) {
            const [path, queryString] = decodedRedirect.split("?");
            const query = queryString
                ? Object.fromEntries(new URLSearchParams(queryString))
                : undefined;

            if (!isRedirectParameterValid(decodedRedirect)) {
                return await navigateToDashboard();
            }

            try {
                return await navigateTo({
                    path,
                    query,
                });
            } catch {
                return await navigateToDashboard();
            }
        }

        return await navigateToDashboard();
    }

    async function navigateToDashboard() {
        return await navigateTo({
            name: "dashboard",
        });
    }

    setToken({
        locale: locale.value,
        refreshToken: refreshToken.value,
        refreshTokenTtl: refreshTokenTtl.value,
        token: token.value,
    });

    return {
        attemptNavigateToRedirect,
        isLoggedIn,
        login,
        logout,
        navigateToLogin,
        payload: readonly(payload),
        refresh,
        refreshIfNecessary,
        refreshToken: readonly(refreshToken),
        setToken,
        token: readonly(token),
    };
});
